import { Box } from '@mui/material'
import { forwardRef, useEffect, useRef } from 'react'
import { BimManager } from '../../visualization/bimManager'
import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '../../store'
import { useSelector } from 'react-redux'
import { BimViewMode } from '../../models/Bim'

const selectedBimObjectSelector = createSelector(
  [(state: RootState) => state.bim],
  (bim) => bim.objects.find((object) => object.id === bim.selectedBimObjectId),
)
const bimViewModeSelector = createSelector(
  [(state: RootState) => state.bim],
  (bim) => bim.viewMode,
)

interface BimElementViewerProps {
  isHidden?: boolean
}

const BimElementViewer = forwardRef<HTMLDivElement, BimElementViewerProps>(
  (props: BimElementViewerProps, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const ifcManager = useRef<BimManager>()
    const selectedBimObject = useSelector(selectedBimObjectSelector)
    const bimViewMode = useSelector(bimViewModeSelector)

    useEffect(() => {
      const initializeIfcManager = async () => {
        const canvas = canvasRef.current
        if (canvas) {
          canvas.width = canvas.parentElement!.clientWidth
          canvas.height = canvas.parentElement!.clientHeight

          ifcManager.current = BimManager.getInstance()
          await ifcManager.current.initialize(canvas)
        }
      }

      initializeIfcManager()

      return () => {
        ifcManager.current?.dispose()
        ifcManager.current = undefined
      }
    }, [])

    useEffect(() => {
      const showObject = async () => {
        await ifcManager.current?.showBimObject(selectedBimObject)
      }

      showObject()
    }, [selectedBimObject, bimViewMode])

    return (
      <Box
        ref={ref}
        sx={{
          display: props.isHidden ? 'none' : 'flex',
          height: '100%',
          width: '100%',
          background: '#FFF',
          border: '1px dashed #BBBCBC',
          borderRadius: '0.3rem',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <Box
          component='img'
          src={selectedBimObject?.thumbnailUrl}
          sx={{
            display: bimViewMode === BimViewMode.Image ? 'block' : 'none',
            maxHeight: '13rem',
            objectFit: 'contain',
            margin: 0,
            padding: 0,
            border: 'none',
            outline: 'none',
            maxWidth: '100%',
          }}
        />
        <canvas
          ref={canvasRef}
          style={{
            display: bimViewMode === BimViewMode.Model3D ? 'block' : 'none',
            width: '100%',
            height: '100%',
            padding: 0,
            margin: 0,
            overflow: 'unset',
            border: 'none',
            outline: 'none',
          }}
        />
      </Box>
    )
  },
)

export default BimElementViewer
